import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import TextField from 'components/control/text-field';
import Button from 'components/control/button';
import { DropdownContext, IDropdownContext } from 'contexts/dropdown';
import { searchFilterIds } from 'constants/test-constants';
import { formatNumber } from '@zoocasa/node-kit/numbers';
import { isStringNumeric } from 'utils/format-number';
import { defaultBuyPriceOptions, defaultRentPriceOptions, defaultSqftOptions } from 'utils/select-options';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';

interface Props {
  min: number | null;
  max: number | null;
  onValueChange: (min: number | null, max: number | null) => void;
  theme?: 'dark' | 'light';
  isRental?: boolean;
  isCurrency?: boolean;
}

const MAX = 'max' as const;
const MIN = 'min' as const;
type PriceType = 'max' | 'min';

export default function FilterDropdownRange({ min, max, onValueChange, theme, isCurrency, isRental }: Props) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const [minNum, setMinNum] = useState(min?.toString());
  const [maxNum, setMaxNum] = useState(max?.toString());
  const { parseCurrency } = useI18nFormatting();
  const [activeType, setActiveType] = useState('');
  const filterMinLabel = isCurrency ? searchFilterIds.priceFilterMinInput : searchFilterIds.sqftFilterMinInput;
  const filterMaxLabel = isCurrency ? searchFilterIds.priceFilterMaxInput : searchFilterIds.sqftFilterMaxInput;
  const filterApplyLabel = isCurrency ? searchFilterIds.priceRangeDropdownApply : searchFilterIds.sqftDropdownApply;

  const { closeDropdown, isActive } = useContext(DropdownContext) as IDropdownContext;

  const priceOptions = isCurrency ? (isRental ? defaultRentPriceOptions : defaultBuyPriceOptions) : defaultSqftOptions;

  useEffect(() => {
    setMinNum(min?.toString());
    setMaxNum(max?.toString());
  }, [min, max]);

  const applyFilter = () => {
    if (isActive) {
      onValueChange(parseCurrency(minNum), parseCurrency(maxNum));
      closeDropdown();
    }
  };

  const setNum = (num: string | null, type: PriceType) => {
    const parsed = parseCurrency(num || '');
    if (parsed !== null && isStringNumeric(parsed.toString())) {
      const formattedNum = formatNumber(parsed);
      if (type === MAX) setMaxNum(formattedNum);
      if (type === MIN) setMinNum(formattedNum);
    } else if (num === '') {
      if (type === MAX) setMaxNum('');
      if (type === MIN) setMinNum('');
    }
  };

  const minPlaceholder = t(LOCALE_SEARCH_PATHS.FILTER_LABELS_NO_MIN, { defaultValue: 'No Min' });
  const maxPlaceholder = t(LOCALE_SEARCH_PATHS.FILTER_LABELS_NO_MAX, { defaultValue: 'No Max' });

  return (
    <div className={buildClassName(styles.component, theme && styles[theme])}>
      <div className={styles['inputs']}>
        <div className={styles['input-container']}>
          <TextField
            placeholder={minPlaceholder}
            title={minPlaceholder}
            testId={filterMinLabel}
            value={minNum || ''}
            onValueChange={value => setNum(value, MIN)}
            onClick={() => setActiveType(MIN)}
            containerClassName={styles['custom-input-container']}
          />
          <NumberDropdownOptionList
            isActive={activeType === MIN}
            isCurrency={isCurrency}
            options={priceOptions || defaultSqftOptions}
            setPrice={setNum}
            type={MIN}
            setActiveType={setActiveType}
            value={minNum}
            maxValue={maxNum}
          />
        </div>
        <small>–</small>
        <div className={styles['input-container']}>
          <TextField
            placeholder={maxPlaceholder}
            title={maxPlaceholder}
            testId={filterMaxLabel}
            value={maxNum}
            onValueChange={value => setNum(value, MAX)}
            onClick={() => setActiveType(MAX)}
            containerClassName={styles['custom-input-container']}
          />
          <NumberDropdownOptionList
            isActive={activeType === MAX}
            isCurrency={isCurrency}
            options={priceOptions || defaultSqftOptions}
            setPrice={setNum}
            type={MAX}
            setActiveType={setActiveType}
            value={maxNum}
            minValue={minNum}
          />
        </div>
      </div>
      <Button label={t(LOCALE_SEARCH_PATHS.FILTER_LABELS_APPLY, { defaultValue: 'Apply' })} className={styles['filter-controls']} theme={'primary'} onClick={applyFilter} testId={filterApplyLabel}/>
    </div>
  );
}

interface PriceOptionsProps {
  options: number[];
  isCurrency: boolean;
  setPrice: (value: string, type: PriceType) => void;
  value: string | undefined;
  type: PriceType;
  setActiveType: (minOrMax: PriceType | '') => void;
  isActive: boolean;
  minValue?: string;
  maxValue?: string;
  isUpSideDown?: boolean;
}

export const NumberDropdownOptionList = ({ options, isCurrency, setPrice, value, type, setActiveType, isActive, minValue, maxValue, isUpSideDown = false }: PriceOptionsProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  const { parseCurrency, formatCurrency } = useI18nFormatting();
  // get rid of inapplicable options
  const getFormattedOptions = () => {
    let formattedOptions;
    if (type === MIN && maxValue) {
      formattedOptions = options.filter(option => option > 0 && option < (parseCurrency(maxValue))).slice(0, 10);
    } else if (type === MAX && minValue) {
      formattedOptions = options.filter(option => option > 0 && option > (parseCurrency(minValue) || 0)).slice(0, 10);
    } else {
      formattedOptions = options.slice(0, 10);
    }
    return isUpSideDown ? formattedOptions.reverse() : formattedOptions;
  };

  return (
    <div className={buildClassName(styles['options'], isActive && styles.active, isUpSideDown && styles['up-side-down'])} data-testid={type === MIN ? searchFilterIds.minSqftFilterOptions : searchFilterIds.maxSqftFilterOptions}>
      {isUpSideDown &&
        <span
          key={'any'}
          onClick={() => {
            setPrice('', type);
            setActiveType('');
          }}
        >
          {/* Any {isCurrency && 'Price'} */}
        </span>
      }
      {getFormattedOptions().map(option => (
        <span
          key={option}
          className={buildClassName(option === parseInt(value || '') && styles['active-option'])}
          onClick={() => {
            setPrice(option.toString(), type);
            setActiveType('');
          }}
        >
          {isCurrency ? formatCurrency(option) : formatNumber(option)}
        </span>
      ))}
      {!isUpSideDown &&
        <span
          key={'any'}
          onClick={() => {
            setPrice('', type);
            setActiveType('');
          }}
        >
          {isCurrency 
            ? t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ANY_PRICE, { defaultValue: 'Any Price' })
            : t(LOCALE_SEARCH_PATHS.FILTER_LABELS_ANY_SQFT, { defaultValue: 'Any Sqft' })
          }
        </span>
      }
    </div>
  );
};
